// src/components/widget/ToggleTagGroupPanel.jsx
import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Tag from "common/src/components/widget/Tag";
import Button from "common/src/components/widget/Button";
import { useTranslation } from "react-i18next";

const ToggleTagGroupPanel = ({
  label = "Items",
  allItems = [],
  excludedItems = [],
  onToggleItem, // (item) => void - move single item between lists
  // new, explicit names (preferred)
  onIncludeAllSelectedFromExclude,
  onExcludeAllSelectedFromInclude,
  // old fallbacks for compatibility
  onIncludeAll,
  onExcludeAll,
  onFlipSelection, // (kept for compatibility)
  id = "id",
  labelKeys = ["name"],
  labelSeparator = " ",
  tooltipFormatter = () => null,
  className = "",
  // NEW: custom label function(s)
  labelFunction,
  displayFunction,
  // NEW: controlled selection from parent (optional)
  controlledSelectedIncludedIds,
  controlledSelectedExcludedIds,
  onChangeSelectedIncludedIds,
  onChangeSelectedExcludedIds,
}) => {
  const { t } = useTranslation();

  // Choose the effective label function if provided
  const effectiveLabelFn = useMemo(() => {
    if (typeof labelFunction === "function") return labelFunction;
    if (typeof displayFunction === "function") return displayFunction;
    return null;
  }, [labelFunction, displayFunction]);

  // --- helpers ---
  const itemId = useCallback(
      (item) => {
        if (!item) return null;
        const raw = item[id];
        return raw === null || raw === undefined ? null : String(raw);
      },
      [id]
  );

  const isExcluded = useCallback(
      (item) => {
        const k = itemId(item);
        if (k === null) return false;
        return excludedItems.some((i) => itemId(i) === k);
      },
      [excludedItems, itemId]
  );

  const displayLabel = useCallback(
      (item) => {
        // 1) Custom function wins
        if (effectiveLabelFn) {
          try {
            const val = effectiveLabelFn(item);
            if (val === null || val === undefined) return "";
            return String(val);
          } catch {
            // fall through to fallback path
          }
        }
        // 2) Fallback to labelKeys join
        const joined = labelKeys
        .map((k) => item?.[k])
        .filter((v) => v !== undefined && v !== null && v !== "")
        .join(labelSeparator);
        if (joined && joined.length > 0) return joined;

        // 3) Final fallback: the item id
        const k = itemId(item);
        return k ?? "";
      },
      [effectiveLabelFn, labelKeys, labelSeparator, itemId]
  );

  // Compose a tooltip for each tag (item info + action hint)
  const itemActionTooltip = useCallback(
      (item) => {
        const base = tooltipFormatter?.(item);
        const hint = isExcluded(item)
            ? t("components.toggleTagGroupPanel.tooltips.tagActionIncludeOne")
            : t("components.toggleTagGroupPanel.tooltips.tagActionExcludeOne");
        return base ? `${base} · ${hint}` : hint;
      },
      [tooltipFormatter, isExcluded, t]
  );

  // Split into included/excluded (filter out items without valid id)
  const included = useMemo(
      () =>
          allItems.filter((item) => {
            const k = itemId(item);
            return k !== null && !isExcluded(item);
          }),
      [allItems, isExcluded, itemId]
  );
  const excluded = useMemo(
      () =>
          allItems.filter((item) => {
            const k = itemId(item);
            return k !== null && isExcluded(item);
          }),
      [allItems, isExcluded, itemId]
  );

  // --- selection state (by ids strings) ---
  const [uncontrolledIncludedIds, setUncontrolledIncludedIds] = useState(new Set());
  const [uncontrolledExcludedIds, setUncontrolledExcludedIds] = useState(new Set());

// prefer controlled sets when provided
  const selectedIncludedIds = controlledSelectedIncludedIds ?? uncontrolledIncludedIds;
  const selectedExcludedIds = controlledSelectedExcludedIds ?? uncontrolledExcludedIds;

// prefer controlled setters when provided
  const setSelectedIncludedIds = onChangeSelectedIncludedIds ?? setUncontrolledIncludedIds;
  const setSelectedExcludedIds = onChangeSelectedExcludedIds ?? setUncontrolledExcludedIds;

  const prevIncludedKeysRef = useRef(new Set());
  const prevExcludedKeysRef = useRef(new Set());
  const didMountRef = useRef(false);
  // Auto-select newly added items (preserve existing selection)
  useEffect(() => {
    setSelectedIncludedIds((prevSet) => {
      const next = new Set(prevSet);
      const currentKeys = new Set(included.map(itemId).filter(Boolean));
      const prevKeys = prevIncludedKeysRef.current || new Set();

      // ✅ Skip auto-selection on initial mount
      if (didMountRef.current) {
        for (const k of currentKeys) {
          if (!prevKeys.has(k)) next.add(k);
        }
        for (const k of Array.from(next)) {
          if (!currentKeys.has(k)) next.delete(k);
        }
      }

      prevIncludedKeysRef.current = currentKeys;
      return next;
    });
  }, [included, itemId]);

  useEffect(() => {
    setSelectedExcludedIds((prevSet) => {
      const next = new Set(prevSet);
      const currentKeys = new Set(excluded.map(itemId).filter(Boolean));
      const prevKeys = prevExcludedKeysRef.current || new Set();

      // ✅ Skip auto-selection on initial mount
      if (didMountRef.current) {
        for (const k of currentKeys) {
          if (!prevKeys.has(k)) next.add(k);
        }
        for (const k of Array.from(next)) {
          if (!currentKeys.has(k)) next.delete(k);
        }
      }

      prevExcludedKeysRef.current = currentKeys;
      return next;
    });
  }, [excluded, itemId]);
// Mark that the component has mounted
  useEffect(() => {
    didMountRef.current = true;
  }, []);
  const includedById = useMemo(() => {
    const m = new Map();
    for (const it of included) {
      const k = itemId(it);
      if (k !== null) m.set(k, it);
    }
    return m;
  }, [included, itemId]);

  const excludedById = useMemo(() => {
    const m = new Map();
    for (const it of excluded) {
      const k = itemId(it);
      if (k !== null) m.set(k, it);
    }
    return m;
  }, [excluded, itemId]);

  // Toggling selection within a box
  const toggleIncludedSelect = useCallback(
      (item) => {
        const key = itemId(item);
        if (key === null) return;
        setSelectedIncludedIds((prev) => {
          const next = new Set(prev);
          next.has(key) ? next.delete(key) : next.add(key);
          return next;
        });
      },
      [itemId]
  );
  const toggleExcludedSelect = useCallback(
      (item) => {
        const key = itemId(item);
        if (key === null) return;
        setSelectedExcludedIds((prev) => {
          const next = new Set(prev);
          next.has(key) ? next.delete(key) : next.add(key);
          return next;
        });
      },
      [itemId]
  );

  const selectedIncluded = useMemo(
      () => Array.from(selectedIncludedIds).map((k) => includedById.get(k)).filter(Boolean),
      [selectedIncludedIds, includedById]
  );

  const selectedExcluded = useMemo(
      () => Array.from(selectedExcludedIds).map((k) => excludedById.get(k)).filter(Boolean),
      [selectedExcludedIds, excludedById]
  );

  // Single move (icon click on Tag)
  const handleToggleItem = useCallback(
      (item) => {
        const key = itemId(item);
        if (key === null) return;
        const wasExcluded = isExcluded(item);

        onToggleItem?.(item);

        // do not carry selection to the other side (per your note)
        if (wasExcluded) {
          setSelectedExcludedIds((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        } else {
          setSelectedIncludedIds((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
          });
        }
      },
      [isExcluded, itemId, onToggleItem]
  );

  // Choose effective callbacks (support both new explicit name and old fallback)
  const cbIncludeFromExclude = onIncludeAllSelectedFromExclude || onIncludeAll;
  const cbExcludeFromInclude = onExcludeAllSelectedFromInclude || onExcludeAll;

  // Move selected excluded -> included (left arrow)
  const handleIncludeSelected = useCallback(() => {
    if (!selectedExcluded || selectedExcluded.length === 0) return;
    if (cbIncludeFromExclude) cbIncludeFromExclude(selectedExcluded);
    // clear selections (do not keep selection on other side)
    setSelectedExcludedIds(new Set());
    setSelectedIncludedIds(new Set());
  }, [cbIncludeFromExclude, selectedExcluded]);

  // Move selected included -> excluded (right arrow)
  const handleExcludeSelected = useCallback(() => {
    if (!selectedIncluded || selectedIncluded.length === 0) return;
    if (cbExcludeFromInclude) cbExcludeFromInclude(selectedIncluded);
    // clear selections
    setSelectedIncludedIds(new Set());
    setSelectedExcludedIds(new Set());
  }, [cbExcludeFromInclude, selectedIncluded]);

  // Flip: move selected included -> excluded and selected excluded -> included
  const handleFlipSelected = useCallback(() => {
    const hasAny =
        (selectedIncluded && selectedIncluded.length > 0) ||
        (selectedExcluded && selectedExcluded.length > 0);
    if (!hasAny) return;

    if (selectedIncluded && selectedIncluded.length > 0) {
      if (cbExcludeFromInclude) cbExcludeFromInclude(selectedIncluded);
    }
    if (selectedExcluded && selectedExcluded.length > 0) {
      if (cbIncludeFromExclude) cbIncludeFromExclude(selectedExcluded);
    }

    // Clear selections after swap
    setSelectedIncludedIds(new Set());
    setSelectedExcludedIds(new Set());
  }, [selectedIncluded, selectedExcluded, cbExcludeFromInclude, cbIncludeFromExclude]);

  // Per-box selection helpers
  const selectAllIncluded = useCallback(() => {
    setSelectedIncludedIds(new Set(included.map((x) => itemId(x)).filter(Boolean)));
  }, [included, itemId]);

  const deselectAllIncluded = useCallback(() => {
    setSelectedIncludedIds(new Set());
  }, []);

  const invertSelectionIncluded = useCallback(() => {
    setSelectedIncludedIds((prev) => {
      const next = new Set();
      for (const it of included) {
        const key = itemId(it);
        if (key && !prev.has(key)) next.add(key);
      }
      return next;
    });
  }, [included, itemId]);

  const selectAllExcluded = useCallback(() => {
    setSelectedExcludedIds(new Set(excluded.map((x) => itemId(x)).filter(Boolean)));
  }, [excluded, itemId]);

  const deselectAllExcluded = useCallback(() => {
    setSelectedExcludedIds(new Set());
  }, []);

  const invertSelectionExcluded = useCallback(() => {
    setSelectedExcludedIds((prev) => {
      const next = new Set();
      for (const it of excluded) {
        const key = itemId(it);
        if (key && !prev.has(key)) next.add(key);
      }
      return next;
    });
  }, [excluded, itemId]);

  return (
      <div
          className={
              "p-2 toggle-tag-panel is-flex is-flex-wrap-wrap is-align-items-start is-justify-content-space-between " +
              className
          }
      >
        {/* Included */}
        <div
            className="tag-panel-box is-flex-grow-1 mr-2"
            style={{ flexBasis: "40%", minWidth: "300px" }}
        >
          <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
            <strong>{t("dashboards.pki.filter.included")}</strong>
            <div className="buttons are-small">
              <Button
                  className="button is-light is-small"
                  onClick={selectAllIncluded}
                  icon="fz fz-check-square"
                  title={t("components.toggleTagGroupPanel.tooltips.selectAllIncluded")}
                  aria-label={t("components.toggleTagGroupPanel.tooltips.selectAllIncluded")}
              >
                {t("common.select_all")}
              </Button>

              <Button
                  className="button is-light is-small"
                  onClick={deselectAllIncluded}
                  icon="fz fz-empty-set"
                  title={t("components.toggleTagGroupPanel.tooltips.selectNoneIncluded")}
                  aria-label={t("components.toggleTagGroupPanel.tooltips.selectNoneIncluded")}
              >
                {t("common.select_none")}
              </Button>

              <Button
                  className="button is-light is-small"
                  onClick={invertSelectionIncluded}
                  icon="fz fz-level-up"
                  title={t("components.toggleTagGroupPanel.tooltips.invertSelectionIncluded")}
                  aria-label={t("components.toggleTagGroupPanel.tooltips.invertSelectionIncluded")}
              >
                {t("common.invert_selection")}
              </Button>
            </div>
          </div>

          <div className="field has-float-label loadable">
            <div
                className="card is-tags is-shadowless tags fixed-grid has-3-cols p-2"
                style={{ minHeight: "140px" }}
            >
              {included.length > 0 ? (
                  included.map((item) => {
                    const key = itemId(item);
                    if (!key) return null;
                    const isSelected = selectedIncludedIds.has(key);
                    return (
                        <Tag
                            className={`cell m-2 ${isSelected ? "is-selected" : ""}`}
                            key={`included-${key}`}
                            label={displayLabel(item)}
                            tooltip={tooltipFormatter(item)}
                            icon="fz fz-arrow-right-to-bracket"
                            clickTarget="both"
                            onTagClick={() => toggleIncludedSelect(item)}
                            onIconClick={() => handleToggleItem(item)}
                        />
                    );
                  })
              ) : (
                  <span className="has-text-grey-light m-2"></span>
              )}
            </div>
          </div>
        </div>

        {/* Controls between lists */}
        <div
            className="tag-panel-controls is-flex is-flex-direction-column is-align-items-center is-justify-content-flex-start mt-2 mr-2"
            style={{ flexBasis: "60px" }}
        >
          <Button
              className="button is-small mb-2"
              onClick={(e) => {
                e.preventDefault();
                handleFlipSelected();
                e.stopPropagation();
              }}
              icon={["fz fz-chevron-left", "fz fz-chevron-right"]}
              disabled={selectedIncluded.length === 0 && selectedExcluded.length === 0}
              title={t("components.toggleTagGroupPanel.tooltips.flipSelected")}
              aria-label={t("components.toggleTagGroupPanel.tooltips.flipSelected")}
          >
            ⇄
          </Button>

          <Button
              className="button is-small mb-2"
              disabled={selectedExcluded.length === 0}
              icon="fz fz-chevron-left"
              onClick={(e) => {
                e.preventDefault();
                handleIncludeSelected();
                e.stopPropagation();
              }}
              title={t("components.toggleTagGroupPanel.tooltips.moveSelectedLeft")}
              aria-label={t("components.toggleTagGroupPanel.tooltips.moveSelectedLeft")}
          />
          <Button
              className="button is-small mb-2"
              disabled={selectedIncluded.length === 0}
              onClick={(e) => {
                e.preventDefault();
                handleExcludeSelected();
                e.stopPropagation();
              }}
              icon="fz fz-chevron-right"
              title={t("components.toggleTagGroupPanel.tooltips.moveSelectedRight")}
              aria-label={t("components.toggleTagGroupPanel.tooltips.moveSelectedRight")}
          />

          <div />
        </div>

        {/* Excluded */}
        <div
            className="tag-panel-box is-flex-grow-1"
            style={{ flexBasis: "40%", minWidth: "300px" }}
        >
          <div className="is-flex is-justify-content-space-between is-align-items-center mb-2">
            <strong>{t("dashboards.pki.filter.excluded")}</strong>
            <div className="buttons are-small">
              <Button
                  className="button is-light is-small"
                  onClick={selectAllExcluded}
                  icon="fz fz-check-square"
                  title={t("components.toggleTagGroupPanel.tooltips.selectAllExcluded")}
                  aria-label={t("components.toggleTagGroupPanel.tooltips.selectAllExcluded")}
              >
                {t("common.select_all")}
              </Button>

              <Button
                  className="button is-light is-small"
                  onClick={deselectAllExcluded}
                  icon="fz fz-empty-set"
                  title={t("components.toggleTagGroupPanel.tooltips.selectNoneExcluded")}
                  aria-label={t("components.toggleTagGroupPanel.tooltips.selectNoneExcluded")}
              >
                {t("common.select_none")}
              </Button>

              <Button
                  className="button is-light is-small"
                  onClick={invertSelectionExcluded}
                  icon="fz fz-level-up"
                  title={t("components.toggleTagGroupPanel.tooltips.invertSelectionExcluded")}
                  aria-label={t("components.toggleTagGroupPanel.tooltips.invertSelectionExcluded")}
              >
                {t("common.invert_selection")}
              </Button>
            </div>
          </div>

          <div className="field has-float-label loadable">
            <div
                className="card is-tags is-shadowless tags fixed-grid has-3-cols p-2"
                style={{ minHeight: "140px" }}
            >
              {excluded.length > 0 ? (
                  excluded.map((item) => {
                    const key = itemId(item);
                    if (!key) return null;
                    const isSelected = selectedExcludedIds.has(key);
                    return (
                        <Tag
                            className={`cell m-2 ${isSelected ? "is-selected" : ""}`}
                            key={`excluded-${key}`}
                            label={displayLabel(item)}
                            tooltip={tooltipFormatter(item)}
                            icon="fz fz-chevron-left"
                            clickTarget="both"
                            onTagClick={() => toggleExcludedSelect(item)}
                            onIconClick={() => handleToggleItem(item)}
                        />
                    );
                  })
              ) : (
                  <span className="has-text-grey-light m-2"></span>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

ToggleTagGroupPanel.propTypes = {
  label: PropTypes.string,
  allItems: PropTypes.array.isRequired,
  excludedItems: PropTypes.array.isRequired,
  onToggleItem: PropTypes.func.isRequired,
  // new explicit callbacks
  onIncludeAllSelectedFromExclude: PropTypes.func,
  onExcludeAllSelectedFromInclude: PropTypes.func,
  // old fallbacks
  onIncludeAll: PropTypes.func,
  onExcludeAll: PropTypes.func,
  onFlipSelection: PropTypes.func,
  id: PropTypes.string,
  labelKeys: PropTypes.arrayOf(PropTypes.string),
  labelSeparator: PropTypes.string,
  tooltipFormatter: PropTypes.func,
  className: PropTypes.string,
  // NEW:
  labelFunction: PropTypes.func,
  displayFunction: PropTypes.func, // alias

  controlledSelectedIncludedIds: PropTypes.instanceOf(Set),
  controlledSelectedExcludedIds: PropTypes.instanceOf(Set),
  onChangeSelectedIncludedIds: PropTypes.func,
  onChangeSelectedExcludedIds: PropTypes.func,

};

export default ToggleTagGroupPanel;
